import {
  Component,
  HostListener,
  Renderer2,
  Inject,
  PLATFORM_ID,
  OnInit,
  OnDestroy
} from '@angular/core';

import {
  trigger,
  transition,
  style,
  animate
} from '@angular/animations';

import { isPlatformBrowser } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { User } from '../user/user';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../user/user.service';
import { AppStore } from '../store/app-store';
import { environment } from '../../environments/environment.production';

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    TranslateModule,
    BsDropdownModule,
    CommonModule,
    RouterModule
  ],
  animations: [
    trigger('ulSlide', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('500ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('500ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  mobileMenuOpen = false;
  isFixed = false;
  isDayMode = false;
  user!: User;
  urlData = environment.urlData;
  activeMenu: string | null = null;

  logoLine1 = '';
  logoLine2 = '';
  logoCursorBlink = false;
  logoEasterEggActive = false;
  private logoAnimating = false;
  private logoDecoding = false;
  private logoClickCount = 0;
  private logoClickTimer?: ReturnType<typeof setTimeout>;
  private logoDecodeTimers: ReturnType<typeof setInterval>[] = [];
  private logoTimers: ReturnType<typeof setTimeout>[] = [];
  private routerSub?: Subscription;
  private readonly logoText1 = 'CODES';
  private readonly logoText2 = '& FINANCE';
  private readonly logoDecodeChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#%&*';
  private readonly sectionIds = ['tariffs', 'partners', 'contact'] as const;

  constructor(
    private userService: UserService,
    public translate: TranslateService,
    public authService: AuthService,
    private appStore: AppStore,
    private renderer: Renderer2,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    translate.addLangs([
      'cs', 'sk', 'de', 'en', 'pl', 'uk', 'es', 'hu',
      'sl', 'ro', 'pt', 'no', 'nl', 'it', 'hr', 'fr',
      'eo', 'el', 'bg', 'vi','ru','da','sv','et','lv',
      'lt','tr','mt','fi','ga'
    ]); 
    translate.use('cs');
    translate.setDefaultLang('cs');

    const mode = this.appStore.getDarkMode();
    if (mode !== undefined) {
      this.isDayMode = mode;
    }

    this.setMode();
  }

  ngOnInit(): void {
    this.userService.user$.subscribe(user => {
      if (user) {
        this.user = user;
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      this.startLogoAnimation();
      this.updateActiveMenu();
      this.routerSub = this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ).subscribe(() => this.updateActiveMenu());
    }
  }

  ngOnDestroy(): void {
    this.clearLogoTimers();
    this.clearLogoDecodeTimers();
    if (this.logoClickTimer) {
      clearTimeout(this.logoClickTimer);
    }
    this.routerSub?.unsubscribe();
  }

  isActive(menu: string): boolean {
    return this.activeMenu === menu;
  }

  onSectionClick(section: string): void {
    this.activeMenu = section;
    this.hiddenMenu();
  }

  restartLogoAnimation(): void {
    if (!this.logoAnimating && !this.logoDecoding && isPlatformBrowser(this.platformId)) {
      this.startLogoAnimation();
    }
  }

  onLogoClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    event.preventDefault();
    this.logoClickCount++;

    if (this.logoClickTimer) {
      clearTimeout(this.logoClickTimer);
    }

    this.logoClickTimer = setTimeout(() => {
      if (this.logoClickCount === 1) {
        void this.router.navigateByUrl('/');
      }
      this.logoClickCount = 0;
    }, 500);

    if (this.logoClickCount === 3) {
      this.logoClickCount = 0;
      if (this.logoClickTimer) {
        clearTimeout(this.logoClickTimer);
        this.logoClickTimer = undefined;
      }
      this.triggerLogoEasterEgg();
    }
  }

  private clearLogoTimers(): void {
    this.logoTimers.forEach(timer => clearTimeout(timer));
    this.logoTimers = [];
  }

  private clearLogoDecodeTimers(): void {
    this.logoDecodeTimers.forEach(timer => clearInterval(timer));
    this.logoDecodeTimers = [];
  }

  private schedule(fn: () => void, delay: number): void {
    this.logoTimers.push(setTimeout(fn, delay));
  }

  private startLogoAnimation(): void {
    if (this.logoAnimating || this.logoDecoding) return;
    this.logoAnimating = true;
    this.clearLogoTimers();
    this.logoLine1 = '';
    this.logoLine2 = '';
    this.logoCursorBlink = false;

    this.schedule(() => {
      this.logoCursorBlink = true;
      this.schedule(() => this.typeLogoLine1(0), 800);
    }, 2000);
  }

  private typeLogoLine1(i: number): void {
    if (this.logoDecoding) return;
    if (i < this.logoText1.length) {
      this.logoLine1 += this.logoText1.charAt(i);
      this.schedule(() => this.typeLogoLine1(i + 1), 120);
    } else {
      this.schedule(() => this.typeLogoLine2(0), 300);
    }
  }

  private typeLogoLine2(j: number): void {
    if (this.logoDecoding) return;
    if (j < this.logoText2.length) {
      this.logoLine2 += this.logoText2.charAt(j);
      this.schedule(() => this.typeLogoLine2(j + 1), 120);
    } else {
      this.logoCursorBlink = false;
      this.logoAnimating = false;
    }
  }

  private triggerLogoEasterEgg(): void {
    if (this.logoDecoding) return;

    this.logoDecoding = true;
    this.clearLogoTimers();
    this.clearLogoDecodeTimers();
    this.logoCursorBlink = false;
    this.logoEasterEggActive = true;

    this.decodeLogoText(1, this.logoText1, () => {
      this.schedule(() => {
        this.decodeLogoText(2, this.logoText2, () => {
          this.schedule(() => {
            this.logoEasterEggActive = false;
            this.logoDecoding = false;
            this.logoAnimating = false;
          }, 3000);
        });
      }, 100);
    });
  }

  private decodeLogoText(line: 1 | 2, target: string, cb: () => void): void {
    let iter = 0;
    const int = setInterval(() => {
      const next = target.split('').map((ch, i) => {
        if (i < iter) return ch;
        if (ch === ' ') return ' ';
        return this.logoDecodeChars[Math.floor(Math.random() * this.logoDecodeChars.length)];
      }).join('');

      if (line === 1) {
        this.logoLine1 = next;
      } else {
        this.logoLine2 = next;
      }

      if (iter >= target.length) {
        clearInterval(int);
        this.logoDecodeTimers = this.logoDecodeTimers.filter(t => t !== int);
        if (line === 1) {
          this.logoLine1 = target;
        } else {
          this.logoLine2 = target;
        }
        cb();
      }
      iter += 1 / 3;
    }, 30);

    this.logoDecodeTimers.push(int);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isFixed = false;
    if (this.isHomeRoute()) {
      this.updateActiveFromScroll();
    }
  }

  hiddenMenu() {
    this.mobileMenuOpen = false;
  }

  private isHomeRoute(): boolean {
    const path = this.router.url.split('?')[0].split('#')[0];
    return path === '/' || path === '';
  }

  private updateActiveMenu(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    const primary = urlTree.root.children['primary'];
    const segments = primary?.segments.map(s => s.path) ?? [];
    const fragment = urlTree.fragment;
    const pathname = window.location.pathname;

    if (pathname.startsWith('/calculator')) {
      this.activeMenu = 'calculator';
      return;
    }

    if (segments[0] === 'video-instruction') {
      this.activeMenu = 'navod';
      return;
    }

    if (segments[0] === 'blog') {
      this.activeMenu = 'blog';
      return;
    }

    if (segments.length === 0) {
      if (fragment && (this.sectionIds as readonly string[]).includes(fragment)) {
        this.activeMenu = fragment;
        return;
      }
      this.updateActiveFromScroll();
      return;
    }

    this.activeMenu = null;
  }

  private updateActiveFromScroll(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isHomeRoute()) {
      return;
    }

    const headerOffset = 120;
    let current: string | null = null;

    for (const id of this.sectionIds) {
      const el = document.getElementById(id);
      if (!el) {
        continue;
      }
      if (el.getBoundingClientRect().top <= headerOffset) {
        current = id;
      }
    }

    this.activeMenu = current;
  }

  public logout() {
    this.authService.revokeToken().subscribe(() => {
      this.authService.clearAccessToken();
      this.authService.logout();

      this.userService.getUserInfo().catch(() => {});
    });
  }

  createAuthorString(): string {
    if (!this.user) return '';
    let name = '';

    if (this.user.title_before) {
      const titles = this.user.title_before.split(' ');
      name += titles.map((item, i) =>
        item.includes(',') && i < titles.length - 1 ? item + ', ' : item + ' '
      ).join('');
    }

    name += `${this.user.forename} ${this.user.surname}`;

    if (this.user.title_after) {
      const titles = this.user.title_after.split(' ');
      name += ', ' + titles.map((item, i) =>
        item.includes(',') && i < titles.length - 1 ? item + ', ' : item + ' '
      ).join('');
    }

    return name;
  }


  clear(){
    this.appStore.clearLang();
  }

  toggleMode(): void {
    this.isDayMode = !this.isDayMode;
    this.setMode();
  }

  setMode() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.isDayMode) {
        this.renderer.addClass(document.body, 'night-mode');
      } else {
        this.renderer.removeClass(document.body, 'night-mode');
      }
      this.appStore.saveDarkMode(this.isDayMode.toString());
    }
  }

  switchLang(lang: string) {
    this.appStore.saveLang(lang);
    this.translate.use(lang);
    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.lang = lang;
    }
  }
}